// freeNadLifeOffer.js
// The free-for-life NAD+ offers on the GLP-1 quiz checkouts, sold the way
// newself-newforms sells them (src/checkout/lib/free-nad.js and the landers'
// hand-off in src/launch/model.js there):
//
//   ?offer=free-nad-nasal-life&nad=nasal          one NAD+ Nasal bottle every 3 months, free
//   ?offer=free-nad-nasal-life&nad=injection      NAD+ Injection every 3 months, free
//   ?offer=free-nad-injection-life&nad=injection  the same free NAD+ Injection
//
// Each comes with a 3, 6 or 12-month GLP-1 plan, for as long as that plan is active.
// Both forms are free (Ale, 2026-10-27). The backend gives each away under its own
// offer: the Nasal under free-nad-nasal-life, the Injection under
// free-nad-injection-life. So an Injection pick always goes out under the injection
// offer, the way newself-newforms' landers hand it over.
//
// Everything about the offer is read from the backend, never typed here:
// GET /api/checkout-offers says which GLP-1 products qualify, which plan lengths it
// runs on and at what add-on term, and the free product's internal_id. The product
// itself comes from the catalog by that internal_id, because its object id differs
// between dev and production.
//
// The rules newself-newforms/src/checkout/lib/free-nad.js keeps:
// 1. The free line is sent at the offer's add-on term (3), never at the GLP-1's.
// 2. Its price is 0 and it never moves a total: Klarna approves the amount we send,
//    so a NAD+ price inside it is the shopper paying for the free NAD+.
// 3. The line and the `offer` slug travel together. A free line without the slug,
//    or under an offer the backend does not run, is billed in full.
// 4. One NAD+ per order (Ale, 2026-10-27): the offer's free line is the only NAD+
//    the order holds.

export const FREE_NAD_LIFE_OFFER = 'free-nad-nasal-life';
export const FREE_NAD_INJ_LIFE_OFFER = 'free-nad-injection-life';

// Every NAD+ product. NAD_NASAL_3000 is the free bottle (free-nad-nasal-life's
// free_addon_slugs), NAD the injection (free-nad-injection-life's); NAD_NASAL is the
// shop's nasal.
const NAD_INTERNAL_IDS = ['NAD', 'NAD_NASAL', 'NAD_NASAL_3000'];

// The params a lander hands over. Each page in the quiz passes them on, so the
// checkout at the end still knows the order is on the offer.
const OFFER_QUERY_KEYS = ['med', 'offer', 'nad', 'nadchosen'];

/** The hand-off params on a route query that are set, to pass on to the next page. */
export function offerQuery(query) {
    const out = {};
    OFFER_QUERY_KEYS.forEach((key) => {
        const value = query && query[key];
        if (typeof value === 'string' && value !== '') out[key] = value;
    });
    return out;
}

/**
 * What the link asks for: `{ offer, form }`, or null. `offer` is the offer the
 * free line goes out under: the injection offer for any Injection pick.
 *
 * Both params must be named, as in offerLineFromQuery in newself-newforms. An
 * unknown `?nad=` is null rather than a default: a typo must not ship the wrong
 * form of NAD+.
 */
export function offerLinkRequest(query) {
    const offer = query && query.offer;
    const form = String((query && query.nad) || '').trim().toLowerCase();
    if (offer === FREE_NAD_LIFE_OFFER && form === 'nasal') return { offer, form };
    if ((offer === FREE_NAD_LIFE_OFFER || offer === FREE_NAD_INJ_LIFE_OFFER) && form === 'injection') {
        return { offer: FREE_NAD_INJ_LIFE_OFFER, form };
    }
    return null;
}

/** The offer record from a /api/checkout-offers response, or null when it is not running. */
export function liveOfferBySlug(response, slug, now = Date.now()) {
    const offers = response && Array.isArray(response.offers) ? response.offers : [];
    const offer = offers.find((o) => o && o.slug === slug);
    if (!offer) return null;
    if (offer.valid_upto && new Date(offer.valid_upto).getTime() < now) return null;
    return offer;
}

/**
 * The running offer a link's free line goes out under, or null.
 *
 * While the injection offer is not running, an Injection pick gets the free Nasal,
 * as newself-newforms does (src/launch/model.js, src/summer-sale/lib/nad-offer.js):
 * rule 3 above, a free injection under an offer the backend does not run is billed.
 */
export function offerForLink(response, request) {
    if (!request) return null;
    const offer = liveOfferBySlug(response, request.offer);
    if (offer || request.offer !== FREE_NAD_INJ_LIFE_OFFER) return offer;
    return liveOfferBySlug(response, FREE_NAD_LIFE_OFFER);
}

const sameInternalId = (a, b) =>
    typeof a === 'string' && typeof b === 'string' && a.trim().toUpperCase() === b.trim().toUpperCase();

// Every catalog row, variants included: get-all-products folds doses of one
// medication into the lowest one's `variants` (see catalogIndex.js), and a fold
// that hid a NAD+ product would quietly drop it.
const catalogRows = (products) =>
    (Array.isArray(products) ? products : []).flatMap((p) => [p, ...((p && Array.isArray(p.variants)) ? p.variants : [])]);

/** The active catalog product the offer gives away, or null. */
export function findOfferProduct(products, offer) {
    const wanted = offer && Array.isArray(offer.free_addon_slugs) ? offer.free_addon_slugs[0] : null;
    if (!wanted) return null;
    return catalogRows(products).find((p) => p && p.is_active && sameInternalId(p.internal_id, wanted)) || null;
}

/**
 * The ids of every NAD+ product in the catalog, and of every bundle that ships one
 * (the Total Reset Plans list NAD or NAD_NASAL in `bundle_components`). What "the
 * order already holds a NAD+" is checked against.
 */
export function nadProductIds(products) {
    const isNad = (id) => NAD_INTERNAL_IDS.some((nad) => sameInternalId(id, nad));
    const ids = new Set();
    catalogRows(products).forEach((p) => {
        if (!p || !p.id) return;
        const parts = Array.isArray(p.bundle_components) ? p.bundle_components : [];
        if (isNad(p.internal_id) || parts.some(isNad)) ids.add(p.id);
    });
    return ids;
}

/**
 * The free cart line for this GLP-1 product and plan length, or null.
 *
 * Null on a plan length the offer does not list (monthly today) and for a product
 * it does not qualify, so the line and the slug drop out together and come back
 * when the shopper picks an eligible plan.
 */
export function freeNadLifeLine({ offer, product, glpProductId, glpTerms }) {
    if (!offer || !product || !glpProductId) return null;
    if (!(offer.qualifying_product_ids || []).includes(glpProductId)) return null;
    const terms = Number((offer.free_addon_terms || {})[String(glpTerms)]);
    if (!terms) return null;
    return {
        id: product.id,
        name: product.name,
        category: String((product.category && product.category.primary) || 'WEIGHT').toUpperCase(),
        price: 0,
        retail_price: 0,
        subheader: product.brand_name || 'Compound Medication',
        terms,
        quantity: 1,
        is_addon: true,
        main_type: 0,
        // Display only; the backend prices the line off `offer` + `terms`.
        is_free: true,
        free_for_life: offer.slug,
    };
}

/**
 * The line's name on the summary. Display only.
 * The injection is "NAD+" in the catalog and "NAD+ Injections" on the
 * newself-newforms checkout-v1 summary (src/checkout/content/products.js there),
 * which this follows. "NAD+ Nasal 3000mg (3-month bottle)" reads "NAD+ Nasal".
 */
export function offerLineName(line) {
    if (!line) return '';
    if (line.free_for_life === FREE_NAD_INJ_LIFE_OFFER) return 'NAD+ Injections';
    const full = String(line.name || '');
    return full.replace(/\s*\d[\d,.]*\s*mg\b.*$/i, '').trim() || full;
}

// The checkout copy newself-newforms prints on the same lines (FREE_NAD_LIFE_LABEL,
// FREE_NAD_LIFE_NOTE, FREE_NAD_INJ_LIFE_LABEL and FREE_NAD_INJ_LIFE_NOTE in
// src/checkout/lib/free-nad.js), so the lander, that checkout and this one make the
// shopper the same promise.
export const freeNadLifeLabel = (terms) => `Free for Life · A New Bottle Every ${terms} Months`;

export const FREE_NAD_LIFE_NOTE =
    'NAD+ Nasal Ships Free Every 3 Months for as Long as Your GLP-1 Plan Is Active. It Ends if Your GLP-1 Plan Is Canceled.';

export const FREE_NAD_INJ_LIFE_NOTE =
    'NAD+ Injection Ships Free Every 3 Months for as Long as Your GLP-1 Plan Is Active. It Ends if Your GLP-1 Plan Is Canceled.';

/** What a free line is, under its name. */
export function offerLineLabel(line) {
    if (!line) return '';
    if (line.free_for_life === FREE_NAD_INJ_LIFE_OFFER) return `Free for Life · Ships Every ${line.terms} Months`;
    return freeNadLifeLabel(line.terms);
}

/** What taking a free line means, under its label. */
export function offerLineNote(line) {
    if (!line) return '';
    return line.free_for_life === FREE_NAD_INJ_LIFE_OFFER ? FREE_NAD_INJ_LIFE_NOTE : FREE_NAD_LIFE_NOTE;
}

// The pictures Ale named for these lines on the checkout (2026-10-27): the NAD+ vial
// and the NAD+ Nasal bottle. The vial is also the NAD product's shop card art;
// NAD_NASAL_3000 has none, and the bottle is the one checkoutCallCenter.vue shows.
const NAD_INJECTION_CARD_IMAGE = 'https://comnewself.s3.us-east-2.amazonaws.com/public/newself-shop/assets/products/nano_nad_1500/vial-nad-injection.webp';
const NAD_NASAL_CARD_IMAGE = 'https://comnewself.s3.us-east-2.amazonaws.com/public/newself-shop/assets/shop/nasal-nad.webp';

/** The picture beside a free line: the vial for the injection, the bottle for the nasal. */
export function offerLineImage(line) {
    if (!line) return '';
    return line.free_for_life === FREE_NAD_INJ_LIFE_OFFER ? NAD_INJECTION_CARD_IMAGE : NAD_NASAL_CARD_IMAGE;
}
